import { useEffect, useRef, useState, FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { RotateCcw } from "lucide-react";

export const VEYA_PAGES = [
  { to: "/veya-how-it-works", label: "How it works" },
  { to: "/veya-trip-check", label: "Trip checks" },
  { to: "/veya-monitoring", label: "Monitoring" },
  { to: "/veya-faq", label: "FAQ" },
];

export const SOURCES = [
  { name: "Expedia newsroom - Romie announcement (May 2024)", url: "https://www.expedia.com/newsroom/spring-product-release-2024/" },
  { name: "Hotel News Resource - Expedia on Romie being discontinued (June 2026)", url: "https://www.hotelnewsresource.com/article141624.html" },
  { name: "Mindtrip", url: "https://mindtrip.ai/" },
  { name: "Mindtrip traveller FAQs", url: "https://resources.mindtrip.ai/travelers/help/traveler-faqs" },
  { name: "Poke docs", url: "https://poke.com/docs" },
  { name: "Instinct", url: "https://instinct.com/" },
];
export const SOURCES_CHECKED = "7 October 2026";

export const useMeta = (title: string, description: string) => {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    const prev = tag.getAttribute("content");
    tag.setAttribute("content", description);
    window.scrollTo(0, 0);
    return () => { if (prev) tag?.setAttribute("content", prev); };
  }, [title, description]);
};

const btn =
  "inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const VeyaHeader = () => (
  <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md">
    <nav aria-label="veya" className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4 mobile:px-4">
      <div className="flex items-baseline gap-2">
        <Link to="/" className="font-funnel text-2xl font-bold text-foreground">qwip</Link>
        <Link to="/veya" className="font-cormorant text-xl italic text-muted-foreground hover:text-foreground">veya</Link>
      </div>
      <ul className="flex items-center gap-6 text-sm text-muted-foreground mobile:hidden">
        {VEYA_PAGES.map((p) => (
          <li key={p.to}><Link to={p.to} className="hover:text-foreground">{p.label}</Link></li>
        ))}
      </ul>
      <a href="/veya#early-access" className={btn}>Get early access</a>
    </nav>
    <ul className="hidden gap-5 overflow-x-auto px-4 pb-3 text-sm text-muted-foreground mobile:flex">
      {VEYA_PAGES.map((p) => (
        <li key={p.to} className="shrink-0"><Link to={p.to} className="hover:text-foreground">{p.label}</Link></li>
      ))}
    </ul>
  </header>
);

export const VeyaFooter = () => (
  <footer className="border-t border-border/60 bg-muted/30">
    <div className="mx-auto grid max-w-6xl grid-cols-3 gap-10 px-6 py-14 text-sm mobile:grid-cols-1 mobile:px-4">
      <div>
        <p className="font-funnel text-xl font-bold">veya <span className="font-sans text-sm font-normal text-muted-foreground">by qwip</span></p>
        <p className="mt-3 max-w-xs text-muted-foreground">Checks whether the trip you've booked fits together. In development.</p>
        <Link to="/" className="mt-4 inline-block underline underline-offset-4">Visit qwip.co.uk</Link>
      </div>
      <div>
        <p className="font-medium">veya</p>
        <ul className="mt-3 space-y-2 text-muted-foreground">
          <li><Link to="/veya" className="hover:text-foreground">Overview</Link></li>
          {VEYA_PAGES.map((p) => <li key={p.to}><Link to={p.to} className="hover:text-foreground">{p.label}</Link></li>)}
          <li><Link to="/privacy-policy" className="hover:text-foreground">Privacy</Link></li>
        </ul>
      </div>
      <div>
        <p className="font-medium">Comparison sources</p>
        <ul className="mt-3 space-y-2 text-muted-foreground">
          {SOURCES.map((s) => <li key={s.url}><a href={s.url} target="_blank" rel="noreferrer" className="hover:text-foreground">{s.name}</a></li>)}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">Checked {SOURCES_CHECKED}.</p>
      </div>
    </div>
    <p className="pb-8 text-center text-xs text-muted-foreground">© {new Date().getFullYear()} qwip</p>
  </footer>
);

export const VeyaLayout = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen bg-background font-sans text-foreground">
    <VeyaHeader />
    <main>{children}</main>
    <VeyaFooter />
  </div>
);

export const Section = ({ id, children, className = "" }: { id?: string; children: ReactNode; className?: string }) => (
  <section id={id} className={`mx-auto max-w-6xl scroll-mt-24 px-6 py-24 mobile:px-4 mobile:py-16 ${className}`}>{children}</section>
);

export const H2 = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <h2 className={`font-funnel text-4xl font-bold leading-tight tracking-tight mobile:text-3xl ${className}`}>{children}</h2>
);

export const SubPageHero = ({ eyebrow, title, intro }: { eyebrow: string; title: string; intro: string }) => (
  <Section className="pb-10">
    <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">{eyebrow}</p>
    <h1 className="mt-4 max-w-3xl font-funnel text-5xl font-bold leading-[1.05] tracking-tight mobile:text-4xl">{title}</h1>
    <p className="mt-6 max-w-2xl text-lg text-muted-foreground">{intro}</p>
  </Section>
);

export const OtherPages = ({ current }: { current: string }) => (
  <Section className="pt-0">
    <div className="rounded-3xl bg-card p-8 shadow-[0_4px_24px_-10px_rgba(31,61,50,0.12)] mobile:p-6">
      <Link to="/veya" className="text-sm underline underline-offset-4">Back to veya overview</Link>
      <div className="mt-6 flex flex-wrap gap-3">
        {VEYA_PAGES.filter((p) => p.to !== current).map((p) => (
          <Link key={p.to} to={p.to} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-muted">{p.label}</Link>
        ))}
        <a href="/veya#early-access" className={btn}>Get early access</a>
      </div>
    </div>
  </Section>
);

/* ---------- Sample conversation ---------- */

type Msg = { from: "you" | "veya"; text: string };
const CONVO: Msg[] = [
  { from: "you", text: "Can you check my Tokyo trip? I've booked my flight and hotel." },
  { from: "veya", text: "I found both. Your flight lands at 21:40, and your hotel says reception closes at 22:00. I don't see an airport transfer in the details you shared." },
  { from: "you", text: "Is that cutting it close?" },
  { from: "veya", text: "Possibly. I'd confirm late check-in with the hotel and plan your airport journey before you fly. Want me to look at the options?" },
  { from: "veya", text: "One more thing: this flight has arrived late on 4 of the past 7 days. That doesn't mean yours will be delayed, but it may be worth leaving extra time for your transfer." },
];

const prefersReduced = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const SampleConversation = () => {
  const [shown, setShown] = useState(prefersReduced() ? CONVO.length : 0);
  const [run, setRun] = useState(0);

  useEffect(() => {
    if (prefersReduced()) { setShown(CONVO.length); return; }
    setShown(0);
    const timers = CONVO.map((_, i) => window.setTimeout(() => setShown(i + 1), 600 + i * 1500));
    return () => timers.forEach(clearTimeout);
  }, [run]);

  return (
    <figure className="mx-auto w-full max-w-[340px]">
      <figcaption className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <span className="rounded-full border border-border bg-card px-3 py-1 font-medium uppercase tracking-wider">Sample conversation</span>
        <button type="button" onClick={() => setRun((r) => r + 1)} className="inline-flex items-center gap-1 rounded-full px-2 py-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Replay conversation
        </button>
      </figcaption>
      <div className="rounded-[3rem] bg-foreground p-3 shadow-[0_24px_60px_-20px_rgba(31,61,50,0.35)] transition-transform duration-500 hover:-rotate-1">
        <div className="flex h-[560px] flex-col overflow-hidden rounded-[2.2rem] bg-background">
          <div className="border-b border-border px-5 pb-3 pt-6 text-center">
            <p className="text-sm font-semibold">veya</p>
          </div>
          <ol className="flex flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-4" aria-live="polite">
            {CONVO.slice(0, shown).map((m, i) => (
              <li key={`${run}-${i}`} className={`flex flex-col veya-msg ${m.from === "you" ? "items-end" : "items-start"}`}>
                <span className="sr-only">{m.from === "you" ? "Traveller:" : "veya:"}</span>
                <p className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13.5px] leading-snug ${m.from === "you" ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md bg-muted text-foreground"}`}>
                  {m.text}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <noscript>
        <ol>{CONVO.map((m, i) => <li key={i}>{m.from === "you" ? "Traveller" : "veya"}: {m.text}</li>)}</ol>
      </noscript>
    </figure>
  );
};

/* ---------- Early access form ---------- */

export const EarlyAccessForm = ({ source = "veya" }: { source?: string }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [err, setErr] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean) || clean.length > 255) {
      setErr("Please enter a valid email address."); setState("error"); return;
    }
    setState("sending");
    const { error } = await supabase.from("veya_waitlist" as never).insert({
      email: clean, source, user_agent: navigator.userAgent.slice(0, 500),
    } as never);
    if (error && error.code !== "23505") {
      setErr("Error - contact support"); setState("error"); return;
    }
    setState("done");
  };

  if (state === "done") {
    return <p role="status" className="rounded-xl border border-border bg-card px-5 py-4">Thanks. You're on the list, and we'll email you when there's something to try.</p>;
  }
  return (
    <form onSubmit={submit} className="w-full max-w-md" noValidate>
      <label htmlFor={`veya-email-${source}`} className="sr-only">Email address</label>
      <div className="flex gap-2 mobile:flex-col">
        <input id={`veya-email-${source}`} type="email" required autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
          className="flex-1 rounded-full border border-input bg-card px-5 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        <button type="submit" disabled={state === "sending"} className={`${btn} py-3 disabled:opacity-60`}>
          {state === "sending" ? "Sending..." : "Get early access"}
        </button>
      </div>
      {state === "error" && <p role="alert" className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">{err}</p>}
      <p className="mt-3 text-xs text-muted-foreground">We only use your email to tell you about veya. No spam, and you can ask us to remove it at any time.</p>
    </form>
  );
};

export const CheckCard = ({ title, observed, checked, source }: { title: string; observed: string; checked: string; source: string }) => (
  <article className="rounded-3xl bg-card p-6 text-foreground shadow-[0_4px_24px_-10px_rgba(31,61,50,0.18)] transition-transform duration-300 hover:-translate-y-1">
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Illustrative example</p>
    <h3 className="mt-2 font-funnel text-lg font-bold">{title}</h3>
    <dl className="mt-4 space-y-2 text-sm">
      <div><dt className="inline font-medium">Observed: </dt><dd className="inline text-muted-foreground">{observed}</dd></div>
      <div><dt className="inline font-medium">Checked: </dt><dd className="inline text-muted-foreground">{checked}</dd></div>
      <div><dt className="inline font-medium">Source: </dt><dd className="inline text-muted-foreground">{source}</dd></div>
    </dl>
  </article>
);

export const Reveal = ({ children, className = "" }: { children: ReactNode; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setShown(true); return; }
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setShown(true); observer.disconnect(); } },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${shown ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
      {children}
    </div>
  );
};
